import * as React from 'react';
import { FC } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import { useLoyaltyOffersUiCooldownContentQuery } from 'generated/sanity-graphql';
import useMediaQuery, { MediaQuery } from 'hooks/use-media-query';
import { useLoyaltyContext } from 'state/loyalty';
import { useUIContext } from 'state/ui';
import { DIFFERENCE_TIME_UNITS, getDifferenceToNow } from 'utils/dateTime';
import { maybeLocaleRawString, maybeLocaleString } from 'utils/graphql';
import { routes } from 'utils/routing';

import { interpolateSanityKey } from '../utils';

import {
  CTAButton,
  CooldownDrawerContainer,
  Description,
  Image,
  Overlay,
  Terms,
  Title,
} from './loyalty-offers-cooldown-drawer.styled';

const LoyaltyOffersCooldownDrawer: FC = () => {
  const { loyaltyOffersInCooldown, loyaltyUser } = useLoyaltyContext();
  const { buildImageUrl } = useUIContext();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isMobile = useMediaQuery(MediaQuery.Mobile);

  const { data: cooldownContentData } = useLoyaltyOffersUiCooldownContentQuery({
    variables: {
      id: 'feature-loyalty-offers-ui-singleton',
    },
    skip: !loyaltyOffersInCooldown,
  });

  const offerCooldownContent = cooldownContentData?.LoyaltyOffersUI?.globalOfferCooldownContent;

  const cooldownContent = {
    image: buildImageUrl(offerCooldownContent?.image?.locale || ''),
    title: maybeLocaleString(offerCooldownContent?.title) || '',
    description: maybeLocaleRawString(offerCooldownContent?.description) || '',
    buttonText: maybeLocaleString(offerCooldownContent?.actionButton?.buttonText) || '',
    buttonUrl: maybeLocaleString(offerCooldownContent?.actionButton?.buttonPath) || '',
    termsText: maybeLocaleRawString(offerCooldownContent?.terms),
  };

  const handleClick = () => {
    navigate(cooldownContent.buttonUrl);
  };

  const isOffersPage = pathname.includes(routes.offers);
  const showCooldown = loyaltyOffersInCooldown && isOffersPage;
  const diffInMin = getDifferenceToNow(
    DIFFERENCE_TIME_UNITS.MINUTES,
    loyaltyUser?.offerRedemptionAvailableAfter || ''
  );

  return showCooldown ? (
    <Overlay data-testid="offers-cooldown-drawer-overlay">
      <CooldownDrawerContainer data-testid="offers-cooldown-drawer-container">
        <Image src={cooldownContent.image} />
        <Title>{cooldownContent.title}</Title>
        <Description data-testid="offers-cooldown-drawer-description">
          {interpolateSanityKey(cooldownContent.description, 'REMAINING_MINUTES', diffInMin)}
        </Description>
        {isMobile && <CTAButton onClick={handleClick}>{cooldownContent.buttonText}</CTAButton>}
        <Terms>{Terms}</Terms>
      </CooldownDrawerContainer>
    </Overlay>
  ) : null;
};

export default LoyaltyOffersCooldownDrawer;
